/**
 * SA-1110 (design note R1): one IntersectionObserver per scroll root.
 *
 * An element registers a callback that runs once, when it first comes within
 * ROOT_MARGIN of its root; a root's observer is disconnected and forgotten
 * when its last element leaves. `MessageScrollRoot` hands the message pane's
 * scroll box down: `undefined` (no provider) is the viewport, `null` is a
 * pane whose box is not attached yet (the hook waits for it), an element is
 * the box. A thread of dozens of videos holds one observer, and inside the
 * pane's `overflow-y-auto` box the margin is the box's, not the viewport's.
 */
import { createContext, useContext, useEffect, useState, type RefObject } from 'react'

/** The list's rule: react once the element comes within this margin. */
export const ROOT_MARGIN = '200px'

export const MessageScrollRoot = createContext<Element | null | undefined>(undefined)

interface SharedObserver {
  observer: IntersectionObserver
  callbacks: WeakMap<Element, () => void>
  count: number
}

const byRoot = new Map<Element | null, SharedObserver>()

function sharedFor(root: Element | null): SharedObserver {
  const existing = byRoot.get(root)
  if (existing) return existing
  const callbacks = new WeakMap<Element, () => void>()
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) callbacks.get(entry.target)?.()
    }
  }, { root, rootMargin: ROOT_MARGIN })
  const made: SharedObserver = { observer, callbacks, count: 0 }
  byRoot.set(root, made)
  return made
}

/** Calls `onSeen` once, when `element` first comes into view of `root`
 * (`null`: the viewport). Returns the unsubscribe, safe to call twice.
 *
 * Contract (SA-1122): one registration per element per root. A second one
 * for the same pair would replace the first's callback and count the element
 * twice, so the root's observer would outlive its last element; `useSeenOnce`
 * registers each element once, and no other caller exists. */
export function observeOnce(element: Element, root: Element | null, onSeen: () => void): () => void {
  const shared = sharedFor(root)
  let active = true
  const stop = () => {
    if (!active) return
    active = false
    shared.callbacks.delete(element)
    shared.observer.unobserve(element)
    shared.count -= 1
    if (shared.count === 0) {
      shared.observer.disconnect()
      if (byRoot.get(root) === shared) byRoot.delete(root)
    }
  }
  shared.callbacks.set(element, () => {
    stop()
    onSeen()
  })
  shared.count += 1
  shared.observer.observe(element)
  return stop
}

/** True once the element behind `ref` has come into view of the pane's
 * scroll box (or the viewport), and at once where there is no observer. */
export function useSeenOnce(ref: RefObject<Element | null>): boolean {
  const root = useContext(MessageScrollRoot)
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === 'undefined')
  useEffect(() => {
    const node = ref.current
    if (seen || !node || root === null || typeof IntersectionObserver === 'undefined') return
    return observeOnce(node, root ?? null, () => setSeen(true))
  }, [seen, root, ref])
  return seen
}
